"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

/**
 * Confirmation step for a destructive action.
 *
 * Unfollow, remove-bookmark and cancel all fired immediately on click. QA asked
 * for a confirmation on each, citing SCMP:
 *   - "取消按鈕應該要有一個弹窗去問嗰個人係咪確定取消"
 *   - "取消關注要有彈槍 問清楚用戶係咪確認 取消" (raised twice)
 *   - "这列不应该是直接移除，参考SCMP 做法"
 *
 * Built on the same Radix dialog primitives as
 * components/account/subscription/cancel-modal.tsx, which is the existing
 * in-repo precedent for this pattern.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "確認",
  cancelLabel = "取消",
  destructive = true,
  zIndex = 50,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the warning colour. */
  destructive?: boolean;
  /**
   * Stacking order. Raise it when the dialog is opened from something that
   * already sits above 50 — the article comment drawer is at z-80, and at the
   * default the confirmation would open behind it.
   */
  zIndex?: number;
  onConfirm: () => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px]"
          style={{ zIndex }}
        />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 flex w-[calc(100vw-32px)] max-w-[400px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[16px] bg-white px-[28px] pb-[24px] pt-[40px] shadow-xl"
          style={{ borderRadius: "3px", zIndex }}
        >
          <DialogPrimitive.Close
            aria-label="關閉"
            className="absolute right-[16px] top-[16px] inline-flex size-[28px] items-center justify-center transition-colors hover:bg-black/[0.05]"
            style={{
              border: `1px solid rgba(0,0,0,0.15)`,
              borderRadius: "999px",
            }}
          >
            <X
              className="size-[14px]"
              strokeWidth={2}
              color={CORE_COLORS.textSecondary}
            />
          </DialogPrimitive.Close>

          <DialogPrimitive.Title
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: 18,
              fontWeight: 700,
              lineHeight: "26px",
              color: CORE_COLORS.textPrimary,
            }}
          >
            {title}
          </DialogPrimitive.Title>

          {description && (
            <DialogPrimitive.Description
              style={{
                fontFamily: CORE_FONT.sans,
                fontSize: 14,
                lineHeight: "20px",
                color: CORE_COLORS.textSecondary,
              }}
            >
              {description}
            </DialogPrimitive.Description>
          )}

          <div className="mt-[8px] flex items-center justify-end gap-[10px]">
            {/* Both fills travel as custom properties applied by a class. As
                inline `background-color` they outranked any `hover:bg-*`, so
                the cancel button had no hover at all and the confirm button
                only faded via opacity. */}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="bg-[color:var(--rest-bg)] transition-colors duration-150 hover:bg-[#f2f2f4] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--ring)]"
              style={
                {
                  fontFamily: CORE_FONT.sans,
                  fontSize: 14,
                  fontWeight: 700,
                  color: CORE_COLORS.textPrimary,
                  padding: "10px 18px",
                  border: `1px solid rgba(0,0,0,0.3)`,
                  borderRadius: 3,
                  ["--rest-bg" as string]: "#ffffff",
                  ["--ring" as string]: CORE_COLORS.brandGreen,
                } as React.CSSProperties
              }
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
              className="bg-[color:var(--rest-bg)] transition-colors duration-150 hover:bg-[color:var(--hover-bg)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--rest-bg)]"
              style={
                {
                  fontFamily: CORE_FONT.sans,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#ffffff",
                  padding: "10px 18px",
                  // Transparent border so this matches the outlined cancel
                  // button's box exactly. Without it the filled button was
                  // 64x41 against the outlined 66x43 and sat 1px higher, so
                  // moving the cursor between the two looked like the buttons
                  // were jumping.
                  border: "1px solid transparent",
                  borderRadius: 3,
                  ["--rest-bg" as string]: destructive
                    ? CORE_COLORS.brandRed
                    : CORE_COLORS.brandGreen,
                  // No dark-red token in the palette; this is brandRed one
                  // step down in lightness.
                  ["--hover-bg" as string]: destructive
                    ? "#b30a14"
                    : CORE_COLORS.brandGreenDark,
                } as React.CSSProperties
              }
            >
              {confirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
